import { Product } from '../types';

// Window mechanics — single source of truth for the client.
// A product's window opens at windowOpenAt (dropDate kept as fallback/alias)
// and closes permanently at windowCloseAt. Uniform tier has no windows.

export type WindowState = 'upcoming' | 'open' | 'closed';

export const getWindowOpenAt = (product: Product): string | undefined =>
  product.windowOpenAt || product.dropDate || undefined;

export const getWindowCloseAt = (product: Product): string | undefined =>
  product.windowCloseAt || undefined;

export const getWindowState = (product: Product, now: Date = new Date()): WindowState => {
  if (product.tier === 'uniform') return 'open';
  const openAt = getWindowOpenAt(product);
  if (openAt && new Date(openAt) > now) return 'upcoming';
  const closeAt = getWindowCloseAt(product);
  if (closeAt && new Date(closeAt) <= now) return 'closed';
  return 'open';
};

export const isArtifactClaimed = (product: Product): boolean =>
  product.tier === 'artifact' && (product.claimed === true || product.status === 'sold_out');

// An artifact is withdrawn when its window closed unclaimed —
// either stored on the product or derived from the window state
export const isArtifactWithdrawn = (product: Product, now: Date = new Date()): boolean =>
  product.tier === 'artifact' &&
  !isArtifactClaimed(product) &&
  (product.withdrawn === true || getWindowState(product, now) === 'closed');

export const isPurchasable = (product: Product, now: Date = new Date()): boolean => {
  if (product.tier === 'uniform') {
    // Uniform: always purchasable; sold_out remains meaningful as an edge case
    return product.status !== 'sold_out';
  }
  if (product.tier === 'artifact' && isArtifactWithdrawn(product, now)) return false;
  if (isArtifactClaimed(product)) return false;
  if (getWindowState(product, now) !== 'open') return false;
  if (!product.tier) {
    // Legacy products keep stock-based sold_out semantics
    return product.status !== 'sold_out';
  }
  // Fragments inside their window are always purchasable — made to order, no stock
  return true;
};

// Vault membership = closed window, not a manual category
export const isVaultItem = (product: Product, now: Date = new Date()): boolean =>
  product.tier === 'fragment' && getWindowState(product, now) === 'closed';

// "Open until 21 August, 18:00" — a stated fact, not an urgency banner
export const formatWindowClose = (iso: string): string => {
  const date = new Date(iso);
  const day = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${day}, ${time}`;
};

// "August 2026" — for vault captions
export const formatWindowClosedMonth = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
